import React from 'react';
import { ChasStatus } from '../../types/api';

type ChasBadgeProps = {
  chas?: ChasStatus | null;
  size?: 'sm' | 'md';
  /** Show the balance / status text after the card name. */
  showText?: boolean;
  onRefresh?: () => void;
  refreshing?: boolean;
};

/** CHAS badge (CheckLine CHASBalance display name + colour) from the latest med.CHASBalance row. */
export function ChasBadge({ chas, size = 'md', showText = false, onRefresh, refreshing }: ChasBadgeProps) {
  if (!chas) return null;
  const fontSize = size === 'sm' ? 10 : 12;
  const padding = size === 'sm' ? '2px 8px' : '3px 10px';
  const known = chas.known;
  const label = known ? `CHAS ${chas.displayName}` : 'CHAS ?';
  const asOf = chas.dateRequest ? new Date(chas.dateRequest).toLocaleDateString('en-SG') : null;
  const title = [
    known ? `CHAS: ${chas.displayName}${chas.cardType ? ` (${chas.cardType})` : ''}` : 'No CHAS enquiry on record',
    chas.text,
    chas.expiryDate ? `Expiry ${new Date(chas.expiryDate).toLocaleDateString('en-SG')}` : null,
    asOf ? `As of ${asOf}${chas.isToday ? ' (today)' : ''}` : null,
    chas.refreshQueued ? 'Refresh requested; reload in a minute.' : null,
  ]
    .filter(Boolean)
    .join('\n');

  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
      <span
        title={title}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 4,
          padding,
          borderRadius: 12,
          fontSize,
          fontWeight: 700,
          lineHeight: 1.2,
          background: known ? chas.color || '#e2e8f0' : '#f1f5f9',
          color: '#0f172a',
          border: '1px solid rgba(15, 23, 42, 0.2)',
          whiteSpace: 'nowrap',
          opacity: chas.isExpired ? 0.6 : 1,
          textDecoration: chas.isExpired ? 'line-through' : undefined,
        }}
      >
        {label}
        {showText && chas.text ? <span style={{ fontWeight: 500 }}>· {chas.text}</span> : null}
      </span>
      {onRefresh && chas.refreshAvailable ? (
        <button
          type="button"
          onClick={onRefresh}
          disabled={refreshing}
          title="Ask Nemo for the latest CHAS status (background)"
          style={{ fontSize: 11, padding: '1px 6px', borderRadius: 8, border: '1px solid #cbd5e1', background: '#fff', cursor: 'pointer' }}
        >
          {refreshing ? '…' : '↻'}
        </button>
      ) : null}
    </span>
  );
}

/** Small chip on queue cards when the visit has an HSS claim. */
export function HssAppliedChip({ size = 'sm' }: { size?: 'sm' | 'md' }) {
  return (
    <span
      title="Haze Subsidy Scheme (HSS) claim on this visit"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        padding: size === 'sm' ? '2px 8px' : '3px 10px',
        borderRadius: 12,
        fontSize: size === 'sm' ? 10 : 12,
        fontWeight: 700,
        background: '#e0f2fe',
        color: '#075985',
        border: '1px solid #7dd3fc',
        whiteSpace: 'nowrap',
      }}
    >
      HSS
    </span>
  );
}
